import type {
  AbsoluteQuantityAdjustmentResponseDTO,
  AllAdjustmentsResponseDTO,
  Energy,
  MeasureIntegrationDetailsDTO,
  PercentageQuantityAdjustmentResponseDTO,
  Power,
} from '@/src/api/types/procurement'
import type {
  LoadAdjustmentFormTypeEnum,
  RenewableSourceProfile,
} from '@/src/features/procurement-and-markets/load-planning/load-planning-adjustments/LoadPlanningAdjustment.types'
import { toAnnualProductionMwh } from '@/src/features/procurement-and-markets/load-planning/load-planning-adjustments/loadPlanning.mapper'
import {
  type AdjustmentRow,
  AdjustmentWarning,
} from '@/src/features/procurement-and-markets/load-planning/load-planning-adjustments/table/adjustmentTable.types'
import { mapProcurementUnitToFrontend } from '@/src/features/procurement-and-markets/utils/procurementUnit.utils'
import { isNullOrUndefined } from '@/src/shared/utils'
import { formatDateToDayMonthYear } from '@/src/utils/dateUtil'
import { areIntervalsOverlapping } from 'date-fns'
import type { TFunction } from 'i18next'

export const formatAdjustmentType = (type: LoadAdjustmentFormTypeEnum | string, t: TFunction) => {
  const typeKey = type as string

  return t(`components.procurement.forecasting.table.adjustment_type.${typeKey}`)
}

const mapTimeToHourMinute = (time?: string): string | undefined => {
  if (!time) return time
  const [hours, minutes] = time.split(':')
  if (!hours || !minutes) return time
  return `${hours}:${minutes}`
}

const mapAdjustmentToRow = <
  T extends {
    id: string
    name: string
    scope?: 'LOCATION' | 'COMPANY'
    locationId?: number
    createdAt: string
    timeframeStart: string
    timeframeEnd: string
    measureIntegrationDetails?: MeasureIntegrationDetailsDTO
    comment?: string
    baseLoad?: Power
    adjustmentValue?: AbsoluteQuantityAdjustmentResponseDTO['adjustmentValue'] | PercentageQuantityAdjustmentResponseDTO['adjustmentValue']
    applicableWeekdays?: AbsoluteQuantityAdjustmentResponseDTO['applicableWeekdays']
    timeFrom?: string
    timeTo?: string
    percentageAdjustmentValue?: number
    referencePeriodStart?: string
    referencePeriodEnd?: string
    totalAnnualProduction?: Energy
    sourceProfile?: RenewableSourceProfile
  },
>(
  adj: T,
  type: keyof AllAdjustmentsResponseDTO
): AdjustmentRow => {
  const mappedAdjustmentValue = typeof adj.adjustmentValue === 'number' ? adj.adjustmentValue : adj.adjustmentValue?.amount
  const mappedAdjustmentValueUnit =
    typeof adj.adjustmentValue === 'number' ? undefined : mapProcurementUnitToFrontend(adj.adjustmentValue?.unit)
  const isSourcedFromMeasure = adj.measureIntegrationDetails
  return {
    id: adj.id,
    name: adj.name,
    type: isSourcedFromMeasure ? 'measure' : type,
    adjustmentType: type,
    scope: adj.scope,
    locationId: adj.locationId,
    createdAt: adj.createdAt,
    timeframeStart: adj.timeframeStart,
    timeframeEnd: adj.timeframeEnd,
    comment: adj.comment,
    baseLoad: adj.baseLoad?.amount,
    adjustmentValue: mappedAdjustmentValue,
    adjustmentValueUnit: mappedAdjustmentValueUnit,
    applicableWeekdays: adj.applicableWeekdays,
    measureIntegrationDetails: adj.measureIntegrationDetails,
    timeFrom: mapTimeToHourMinute(adj.timeFrom),
    timeTo: mapTimeToHourMinute(adj.timeTo),
    percentageAdjustmentValue: adj.percentageAdjustmentValue,
    referencePeriodStart: adj.referencePeriodStart,
    referencePeriodEnd: adj.referencePeriodEnd,
    totalAnnualProduction: adj.totalAnnualProduction ? toAnnualProductionMwh(adj.totalAnnualProduction) : undefined,
    sourceProfile: adj.sourceProfile,
  }
}

/**
 * Since the backend provides the data by separating the different adjustment into different properties,
 * this method is to return the list flattened into a single-level array, EXCEPT for the measureSimulationCandidates that is not included here on purpose
 */
export const mapAdjustmentsToRows = (adjustments: AllAdjustmentsResponseDTO): AdjustmentRow[] => {
  const rows: AdjustmentRow[] = [
    ...adjustments.companyHolidays.map(adj => mapAdjustmentToRow(adj, 'companyHolidays')),
    ...adjustments.absoluteQuantity.map(adj => mapAdjustmentToRow(adj, 'absoluteQuantity')),
    ...adjustments.percentageQuantity.map(adj => mapAdjustmentToRow(adj, 'percentageQuantity')),
    ...adjustments.flexibility.map(adj => mapAdjustmentToRow(adj, 'flexibility')),
    ...adjustments.renewableProduction.map(adj => mapAdjustmentToRow(adj, 'renewableProduction')),
    ...adjustments.referenceBaseloadCorrection.map(adj => mapAdjustmentToRow(adj, 'referenceBaseloadCorrection')),
  ]

  // Sort by creation date (newest first)
  return rows.sort((a, b) => {
    if (!a.createdAt || !b.createdAt) return 0
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  })
}

interface AdjustmentForCheckingHolidayOverlap {
  id: string
  scope?: 'LOCATION' | 'COMPANY'
  locationId?: number
  timeframeStart: string
  timeframeEnd: string
}

/**
 * A company-scoped adjustment applies to every location, so it is considered to affect the same
 * location as any other adjustment. Otherwise the two must share the same locationId.
 */
const affectsSameLocation = (
  a: { scope?: 'LOCATION' | 'COMPANY'; locationId?: number },
  b: { scope?: 'LOCATION' | 'COMPANY'; locationId?: number }
) => {
  if (a.scope === 'COMPANY' || b.scope === 'COMPANY') return true
  return a.locationId === b.locationId
}

const isAdjustmentOverlapping = (
  a: AdjustmentForCheckingHolidayOverlap | AdjustmentRow,
  b: AdjustmentForCheckingHolidayOverlap | AdjustmentRow
) => {
  return areIntervalsOverlapping(
    { start: new Date(a.timeframeStart), end: new Date(a.timeframeEnd) },
    { start: new Date(b.timeframeStart), end: new Date(b.timeframeEnd) }
  )
}

const isHolidayAdjustmentOverwritten = (holidayAdjustment: AdjustmentRow, otherAdjustments: AdjustmentRow[]) => {
  const overlappingHolidays = otherAdjustments
    .filter(other => other.type === 'companyHolidays' && other.id !== holidayAdjustment.id && !isNullOrUndefined(other.baseLoad))
    .filter(other => isAdjustmentOverlapping(holidayAdjustment, other))
    .filter(other => affectsSameLocation(other, holidayAdjustment))

  const overlappingHolidayBaseLoads = overlappingHolidays.map(holiday => holiday.baseLoad)

  // if the current checked adjustment's base load is bigger than any of its overlap, then it's overwritten
  return overlappingHolidayBaseLoads.some(overlap => holidayAdjustment.baseLoad! > overlap!)
}

const isAffectedByHolidayAdjustment = (adjustment: AdjustmentForCheckingHolidayOverlap, otherAdjustments: AdjustmentRow[]) => {
  const currentAdjustment = otherAdjustments.find(a => a.id === adjustment.id)

  if (currentAdjustment?.type === 'renewableProduction') return false

  // Special check when adjustment is a holiday type to check if any other holiday adjustments is overriding it.
  if (currentAdjustment?.type === 'companyHolidays' && !isNullOrUndefined(currentAdjustment.baseLoad)) {
    return isHolidayAdjustmentOverwritten(currentAdjustment, otherAdjustments)
  }

  return otherAdjustments.some(a => {
    if (a.type !== 'companyHolidays' || adjustment.id === a.id) return false
    if (!affectsSameLocation(adjustment, a)) return false
    return isAdjustmentOverlapping(adjustment, a)
  })
}

export const getOverlappingHolidayTimeframes = (
  adjustment: AdjustmentForCheckingHolidayOverlap,
  allAdjustments: AllAdjustmentsResponseDTO,
  language?: string
): string[] => {
  const overlappingHolidays = allAdjustments.companyHolidays
    .map(a => mapAdjustmentToRow(a, 'companyHolidays'))
    .filter(holidayRow => isAffectedByHolidayAdjustment(adjustment, [holidayRow]))
    .map(a => ({ start: new Date(a.timeframeStart), end: new Date(a.timeframeEnd) }))
    .sort((a, b) => a.start.getTime() - b.start.getTime())

  return overlappingHolidays.map(
    ({ start, end }) => `${formatDateToDayMonthYear(start, language)} - ${formatDateToDayMonthYear(end, language)}`
  )
}

const isMeasureAdjustmentOutdated = (adjustment: AdjustmentRow) => !!adjustment.measureIntegrationDetails?.simulationChangeStatus

const REFERENCE_PERIOD_TYPE: keyof AllAdjustmentsResponseDTO = 'referenceBaseloadCorrection'

/**
 * A location-scoped reference period correction is overridden whenever a company-scoped reference period
 * correction overlaps it in time, since the company-wide one takes precedence at that location.
 */
export const hasOverlappingCompanyReferenceCorrection = (
  adjustment: AdjustmentForCheckingHolidayOverlap,
  allAdjustments: AllAdjustmentsResponseDTO
): boolean => {
  if (adjustment.scope !== 'LOCATION') return false

  return allAdjustments.referenceBaseloadCorrection
    .map(a => mapAdjustmentToRow(a, REFERENCE_PERIOD_TYPE))
    .some(other => other.scope === 'COMPANY' && other.id !== adjustment.id && isAdjustmentOverlapping(adjustment, other))
}

/**
 * A reference period correction is ignored when either:
 * 1. the load planning uses a custom reference base load, or
 * 2. it is location-scoped and a company-scoped reference period correction overlaps it in time.
 */
const isReferencePeriodCorrectionIgnored = (
  adjustment: AdjustmentRow,
  otherAdjustments: AdjustmentRow[],
  hasCustomReferenceBaseLoad: boolean
): boolean => {
  if (adjustment.adjustmentType !== REFERENCE_PERIOD_TYPE) return false

  if (hasCustomReferenceBaseLoad) return true

  if (adjustment.scope !== 'LOCATION') return false

  return otherAdjustments.some(
    other =>
      other.adjustmentType === REFERENCE_PERIOD_TYPE &&
      other.id !== adjustment.id &&
      other.scope === 'COMPANY' &&
      isAdjustmentOverlapping(adjustment, other)
  )
}

export const getAdjustmentWarnings = (
  adjustment: AdjustmentRow,
  otherAdjustments: AdjustmentRow[],
  hasCustomReferenceBaseLoad = false
): AdjustmentWarning[] => {
  const adjustmentWarnings: AdjustmentWarning[] = []

  if (adjustment.adjustmentType === 'renewableProduction') return adjustmentWarnings

  if (isAffectedByHolidayAdjustment(adjustment, otherAdjustments)) {
    adjustmentWarnings.push(AdjustmentWarning.HOLIDAY_OVERLAP)
  }

  if (isReferencePeriodCorrectionIgnored(adjustment, otherAdjustments, hasCustomReferenceBaseLoad)) {
    adjustmentWarnings.push(AdjustmentWarning.REFERENCE_PERIOD_IGNORED)
  }

  if (isMeasureAdjustmentOutdated(adjustment)) {
    adjustmentWarnings.push(AdjustmentWarning.MEASURE_ADJUSTMENT_OUTDATED)
  }

  return adjustmentWarnings
}
